/**
 * One map from a warehouse-account refusal code to a sentence with a next step.
 *
 * Most of Lookout's own refusals are thrown as `new XRefusedError(reason)`, whose `message` IS the
 * reason — `PlatformCreateRefusedError` and `RevokeRefusedError` always, `KeyRotationRefusedError`
 * whenever no message is passed. `mapPlatformError` puts that message on the oRPC error, so anything
 * that prints `err.message` shows the customer `reconnect_target_invalid`, `not_revocable` or
 * `account_not_found` verbatim. QA saw all three.
 *
 * This is the single map (#821 review). Two rules keep it honest:
 *
 * 1. A code is listed here only when the server has no prose of its own. Refusals that DO carry a
 *    written explanation — `shadow_connection`, `key_state_unknown`, `conversion_incomplete`,
 *    `connection_changed`, `connection_differs_from_account`, `shared_snowflake_user`,
 *    `conversion_not_enabled`, `account_in_use`, `rudderstack_delete_forbidden`, `record_failed`,
 *    `rudderstack_read_only`, `retained_account_available` — are deliberately absent,
 *    because that prose is more specific than anything spelled here and often names the remedy.
 * 2. `refusalMessage` refuses to print a bare code even for a reason nobody has added copy for yet,
 *    so a new server-side refusal degrades to a generic sentence instead of leaking its identifier.
 */
import { PLATFORM_REFUSAL_COPY } from '@server/platform-refusal-copy';
import type { WarehouseProblem } from '@server/warehouse-failure';

import { hasOrpcCode, orpcErrorMessage } from '@/_shared/lib/orpc-errors';

export const REASON_COPY: Record<string, string> = {
  rudderstack_reauth_required: 'Your RudderStack sign-in has expired. Sign in with RudderStack again.',
  // A RudderStack account create or Reconnect: the server's own words, which it also sends as
  // `data.problem` (ACT2-1077). Kept here for a refusal that reaches the screen without one.
  ...Object.fromEntries(
    Object.entries(PLATFORM_REFUSAL_COPY).map(([reason, copy]) => [reason, `${copy.message} ${copy.nextStep}`]),
  ),
  // Lookout's own pre-flight dial, before RudderStack was asked to create anything. Listed although
  // the server writes prose too: only this copy says that nothing was created, which is the whole
  // reassurance an admin staring at a TLS failure needs.
  lookout_tls_untrusted:
    "Lookout could not verify this server's TLS certificate. Lookout only connects to servers whose certificate is issued by a trusted CA and matches the host — a self-signed or private-CA certificate is not accepted. Nothing was created in RudderStack.",
  lookout_connect_failed:
    'Lookout could not connect to this server. Check the host, port and credentials, and that the server accepts TLS connections. Nothing was created in RudderStack.',
  // Phase 3c, rotation and revoke.
  // Revoke is off on this deployment (`LOOKOUT_PLATFORM_ACCOUNT_REVOKE`). The card does not offer the
  // action at all while it is off, so this copy is for a direct API call — and it says the one true
  // thing an admin can act on: nothing was changed, and Delete is still there.
  revoke_not_enabled:
    'Revoking a RudderStack account from Lookout is not available on this deployment. Nothing was changed. Delete the account in RudderStack, or delete this connection in Lookout.',
  connection_not_found: 'This connection no longer exists. Reload the page.',
  connection_paused:
    'This connection is waiting to be reconnected, so its RudderStack account cannot be changed. Reconnect it first.',
  not_rotatable: 'This connection has no RudderStack-held key to rotate.',
  // QA saw this one raw on a row that cannot be revoked.
  not_revocable:
    'Lookout did not create this connection’s account in RudderStack, so there is nothing here to revoke. Delete the connection instead.',
  // Earlier QA captured this raw during a rotation.
  account_not_found:
    'This connection’s account no longer exists in RudderStack. Revoke the connection and create the account again, or reconnect it.',
  nothing_to_finish: 'This rotation has nothing left to finish. Reload the page to see the connection’s current key.',
  // config-backend failures with no code of their own. A 5xx may still have done its work, so this
  // copy never says that nothing was created. The words do not name the platform (ACT2-1268).
  rudderstack_error: 'Lookout could not finish this request. Try again. If it keeps failing, contact our team.',
  rudderstack_change_rejected: 'Lookout could not finish this request. Try again.',
  rudderstack_denied: 'Your role does not allow this. Ask the admin who manages your team’s roles.',
  // The customer cannot fix a 404 from the platform, so it reads as a server error.
  rudderstack_not_found: 'Lookout could not finish this request. Try again. If it keeps failing, contact our team.',
  rudderstack_conflict: 'This conflicts with an existing account. Change that detail and try again.',
  rudderstack_rejected: 'The account details were not accepted. Check each field and try again.',
  rate_limited: 'Too many requests. Wait one minute, then try again.',
};

/** An error nothing mapped, on create. The same words as a platform 5xx: the admin does the same thing. */
export const UNMAPPED_CREATE_ERROR_COPY =
  'Lookout could not create this account. Try again. If it keeps failing, contact our team.';

/** Create-only copy: the same failures, said about the account the admin is creating. */
const CREATE_COPY: Record<string, string> = {
  rudderstack_error: UNMAPPED_CREATE_ERROR_COPY,
  rudderstack_not_found: UNMAPPED_CREATE_ERROR_COPY,
  validator_unavailable:
    'Lookout could not reach the connection validator. Try again. If it keeps failing, contact our team.',
  validator_timeout:
    'Lookout could not check the connection in time. Try again. If it keeps failing, contact our team.',
  client_timeout: 'Lookout could not check the connection in time. Try again. If it keeps failing, contact our team.',
  rudderstack_denied: 'Your role cannot create warehouse accounts. Ask the admin who manages your team’s roles.',
};

/** Gateways can return plain text instead of the oRPC error envelope. */
export function isGatewayError(err: unknown): boolean {
  if (!err || typeof err !== 'object' || !('status' in err)) return false;
  return err.status === 502 || err.status === 503 || err.status === 504;
}

/** The `WarehouseProblem` a refusal carries on `data.problem`, if the server sent one (ACT2-1077). */
export function refusalProblem(err: unknown): WarehouseProblem | undefined {
  const problem = (err as { data?: { problem?: unknown } } | null)?.data?.problem;
  return problem !== null && typeof problem === 'object' ? (problem as WarehouseProblem) : undefined;
}

/** Remove only a leading platform label; brand words elsewhere may be customer values. */
export function withoutPlatformName(text: string): string {
  return text.replace(/^(?:RudderStack|Rudderstack|RUDDERSTACK)(?: returned| RETURNED)?:\s+/u, '');
}

/** A platform reason quoted inside our sentence: its own final period would print as `.".`. */
function quoted(message: string): string {
  return `"${withoutPlatformName(message).replace(/[.\s]+$/u, '')}"`;
}

/** A message that is nothing but a lowercase identifier — a refusal code that escaped its copy. */
const BARE_CODE = /^[a-z_]+$/;

/**
 * The sentence to show for a failed warehouse-account call.
 *
 * `data.reason` first, then whatever the server wrote, then `fallback` — and never a bare code,
 * whichever of those it came from. An `INTERNAL_SERVER_ERROR` with no reason is an error nothing
 * mapped, and its message is oRPC's generic text, so it gets `fallback` too.
 */
export function refusalMessage(err: unknown, fallback: string, opts: { creatingAccount?: boolean } = {}): string {
  // ACT2-1077: a server that describes its refusal says it best, and its words name the state the
  // screen is in (a paused connection's Reconnect), which a client-side table cannot see.
  const problem = refusalProblem(err);
  if (problem) return `${problem.message} ${problem.nextStep}`;
  const data = (err as { data?: { reason?: unknown; message?: unknown } } | null)?.data;
  const reason = data?.reason;
  const createCopy = opts.creatingAccount && typeof reason === 'string' ? CREATE_COPY[reason] : undefined;
  if (createCopy !== undefined) return createCopy;
  const copy = typeof reason === 'string' ? REASON_COPY[reason] : undefined;
  const platformMessage = typeof data?.message === 'string' && data.message.trim() !== '' ? data.message : null;
  if (platformMessage !== null && reason === 'rudderstack_conflict') {
    return `This conflicts with an existing account: ${quoted(platformMessage)}. Change that detail and try again.`;
  }
  if (platformMessage !== null && reason === 'rudderstack_rejected') {
    return `The account details were not accepted: ${quoted(platformMessage)}. Fix that field and try again.`;
  }
  if (platformMessage !== null && reason === 'rudderstack_change_rejected') {
    return `The change was not accepted: ${quoted(platformMessage)}. Fix that and try again.`;
  }
  if (copy !== undefined) return copy;
  const message = orpcErrorMessage(err, fallback);
  // An error nothing mapped: on create the fallback names the retry; elsewhere oRPC's generic text
  // gives way to the fallback, and a server's own sentence (a keypair save failure) is kept.
  if (reason === undefined && (hasOrpcCode(err, 'INTERNAL_SERVER_ERROR') || isGatewayError(err))) {
    if (opts.creatingAccount || message.toLowerCase() === 'internal server error') return fallback;
  }
  if (message === 'rudderstack_reauth_required') return REASON_COPY[message] ?? fallback;
  return BARE_CODE.test(message) ? fallback : message;
}
